import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { filterByPortals, portalOf } from '../../contexts/MarketContext';
import { useT } from '../../contexts/LocaleContext';
import { CATEGORY_STYLES, CATEGORY_DOT } from '../../utils/statuses';
import { SkeletonList } from '../../components/Skeleton';
import EmptyState from '../../components/EmptyState';
import { totalTravellers } from '../../utils/groupSize';

function formatDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// A booked tour, not a sales lead: unconverted CRM records, cancelled and
// archived tours stay out (same rules as the staff Tours list).
function isLiveTour(r) {
  if (r.archived) return false;
  if (r.crmOrigin && !r.crmFullyConverted) return false;
  if (r.status === 'cancelled' || r.category === 'cancelled') return false;
  return !!r.departureDate;
}

export default function FootballOpsHome() {
  const { userPortals, footballOpsPermissions: perms } = useAuth();
  const navigate = useNavigate();
  const t = useT();
  const [tours, setTours] = useState(null);
  const [when, setWhen] = useState('upcoming');
  const [sport, setSport] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'requests'), (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter(isLiveTour);
      setTours(filterByPortals(docs, userPortals));
    }, () => setTours([]));
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPortals.join(',')]);

  const sports = useMemo(
    () => [...new Set((tours || []).map((t) => t.sport).filter(Boolean))].sort(),
    [tours]
  );

  const shown = useMemo(() => {
    const today = todayIso();
    const q = search.trim().toLowerCase();
    const list = (tours || []).filter((t) => {
      const end = t.returnDate || t.departureDate;
      if (when === 'upcoming' ? end < today : end >= today) return false;
      if (sport !== 'all' && t.sport !== sport) return false;
      if (q && ![t.tourName, t.internalRef, t.destination, t.contactName, t.sport]
        .some((v) => (v || '').toLowerCase().includes(q))) return false;
      return true;
    });
    // Next tour first when looking ahead; most recent first when looking back.
    list.sort((a, b) => (a.departureDate || '').localeCompare(b.departureDate || ''));
    return when === 'upcoming' ? list : list.reverse();
  }, [tours, when, sport, search]);

  const multiPortal = userPortals.length > 1;

  return (
    <div className="max-w-4xl">
      <div className="mb-5">
        <h2 className="text-[22px] font-bold text-[#111827]">{t('footballOps.toursTitle')}</h2>
        <p className="text-[13px] text-[#6B7280] mt-1">{t('footballOps.toursSub')}</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex bg-white border border-[#E5E7EB] rounded-[9px] p-0.5">
          {[['upcoming', t('footballOps.upcoming')], ['past', t('footballOps.past')]].map(([key, label]) => (
            <button key={key} onClick={() => setWhen(key)}
              className={`px-3 py-1.5 text-[12.5px] font-semibold rounded-[7px] transition ${when === key ? 'bg-navy text-white' : 'text-[#6B7280] hover:text-[#111827]'}`}>
              {label}
            </button>
          ))}
        </div>
        {sports.length > 1 && (
          <select value={sport} onChange={(e) => setSport(e.target.value)}
            className="border border-[#E5E7EB] rounded-[9px] px-3 py-[7px] text-[12.5px] bg-white text-[#374151] focus:outline-none focus:ring-2 focus:ring-pb">
            <option value="all">{t('footballOps.allSports')}</option>
            {sports.map((s) => <option key={s} value={s}>{t.value('sport', s)}</option>)}
          </select>
        )}
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('footballOps.searchTours')}
          className="flex-1 min-w-[160px] border border-[#E5E7EB] rounded-[9px] px-3 py-[7px] text-[12.5px] bg-white focus:outline-none focus:ring-2 focus:ring-pb" />
      </div>

      {tours === null ? (
        <SkeletonList count={4} />
      ) : shown.length === 0 ? (
        <EmptyState
          icon={
            <svg className="w-6 h-6 text-[#9CA3AF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
            </svg>
          }
          title={tours.length === 0 ? t('footballOps.noToursTitle') : t('footballOps.noMatchTitle')}
          description={tours.length === 0 ? t('footballOps.noToursDesc') : t('footballOps.noMatchDesc')}
        />
      ) : (
        <div className="space-y-3">
          {shown.map((tour) => {
            const cat = tour.category || 'sales_admin';
            const sportLabel = t.value('sport', tour.sport);
            return (
              <button
                key={tour.id}
                onClick={() => navigate(`tours/${tour.id}`)}
                className="w-full text-left bg-white rounded-[12px] border border-[#E5E7EB] p-5 hover:border-pb/30 hover:shadow-sm transition flex items-center gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <p className="text-[15px] font-bold text-[#111827]">
                      {tour.tourName || `${sportLabel} — ${tour.destination}`}
                    </p>
                    <span className={`inline-flex items-center gap-1 px-[9px] py-[3px] rounded-[12px] text-[11px] font-semibold whitespace-nowrap ${CATEGORY_STYLES[cat] || 'bg-[#F3F4F6] text-[#374151]'}`}>
                      <span className={`w-[5px] h-[5px] rounded-full ${CATEGORY_DOT[cat] || 'bg-[#9CA3AF]'}`} />
                      {t(`category.${cat}`)}
                    </span>
                    {multiPortal && (
                      <span className="px-[9px] py-[3px] rounded-[12px] text-[11px] font-semibold bg-[#F3F4F6] text-[#6B7280] whitespace-nowrap">{t(`portals.${portalOf(tour)}`)}</span>
                    )}
                  </div>
                  <p className="text-[13px] text-[#6B7280]">
                    {tour.tourName && <span>{sportLabel} — {tour.destination} · </span>}
                    {formatDate(tour.departureDate)} – {formatDate(tour.returnDate)}
                    {perms.overview && totalTravellers(tour) ? ` · ${t('footballOps.travellers', { n: totalTravellers(tour) })}` : ''}
                    {perms.overview && tour.numberOfSquads ? ` · ${tour.numberOfSquads === 1 ? t('footballOps.squadOne') : t('footballOps.squadMany', { n: tour.numberOfSquads })}` : ''}
                  </p>
                </div>
                <svg className="w-5 h-5 text-[#D1D5DB] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
